import { forwardRef } from 'react'

import {
  Input as AntInput,
  InputNumber as AntInputNumber,
  InputProps as AntInputProps,
  InputNumberProps as AntInputNumberProps,
  InputRef,
} from 'antd'
import styled, { css } from 'styled-components'

export const baseInputStyles = css`
  color: ${({ theme }) => theme.colour.secondary._900};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  border: 1px solid ${({ theme }) => theme.colour.secondary._400};

  &::placeholder {
    color: ${({ theme }) => theme.colour.secondary._400};
  }

  & input {
    color: ${({ theme }) => theme.colour.secondary._900};

    &::placeholder {
      color: ${({ theme }) => theme.colour.secondary._400};
    }
  }

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colour.secondary._900} !important;
  }
`

type BpInputProps = {
  transparent?: boolean
  $naked?: boolean
}

const BuildPassLookinInput = styled(AntInput)<BpInputProps>`
  ${baseInputStyles}

  ${({ bordered }) =>
    bordered !== false
      ? css`
          &.ant-input-affix-wrapper-focused {
            border-color: ${({ theme }) =>
              theme.colour.secondary._900} !important;
          }
        `
      : css`
          border: none;
        `}

  ${({ transparent }) =>
    transparent &&
    css`
      background: transparent;

      & input {
        background: transparent;
      }
    `}

  ${({ $naked }) =>
    $naked &&
    css`
      border: 0;
      padding: 0;

      &:hover,
      &:focus {
        box-shadow: none;
      }
    `}
`

type Props = AntInputProps & {
  transparent?: boolean
  naked?: boolean
  preventDefault?: boolean
  ref?: React.Ref<InputRef>
}

export type InputProps = Props

export const Input: React.FC<Props> = forwardRef(
  (
    {
      transparent,
      naked,
      preventDefault = true,

      ...rest
    },
    ref
  ) => {
    return (
      <BuildPassLookinInput
        ref={ref}
        onKeyDown={
          preventDefault ? (e) => e.key === 'Enter' && e.preventDefault() : null
        }
        transparent={transparent}
        $naked={naked}
        {...rest}
      />
    )
  }
)

const BuildPassLookinInputNumber = styled(AntInputNumber)<BpInputProps>`
  ${baseInputStyles}

  // hide the inner border when addonBefore or addonAfter is used
  & & {
    border-top: none;
    border-bottom: none;
    border-left: none;
  }
  & .ant-input-number-group-addon {
    border: none;
  }
  // end the above styling

  ${({ transparent }) =>
    transparent &&
    css`
      background: transparent;

      & input {
        background: transparent;
      }
    `}

  ${({ $naked }) =>
    $naked &&
    css`
      border: 0;
      padding: 0;

      &:hover,
      &:focus {
        box-shadow: none;
      }
    `}
`

const BuildPassLookingTextarea = styled(BuildPassLookinInput)<TextAreaProps>`
  ${({ showCount }) =>
    showCount &&
    css`
      border: none;

      & textarea {
        border: 1px solid ${({ theme }) => theme.colour.secondary._400};
      }
    `}
`

export type InputNumberProps = AntInputNumberProps & {
  transparent?: boolean
  naked?: boolean
}

export const InputNumber: React.FC<InputNumberProps> = forwardRef<
  HTMLInputElement,
  InputNumberProps
>(({ transparent, naked, ...rest }, ref) => {
  return (
    <BuildPassLookinInputNumber
      transparent={transparent}
      $naked={naked}
      ref={ref}
      {...rest}
    />
  )
})

export type TextAreaProps = React.ComponentProps<typeof AntInput.TextArea> & {
  transparent?: boolean
  naked?: boolean
}

export const TextArea: React.FC<TextAreaProps> = forwardRef(
  ({ transparent, naked, ...rest }, ref) => {
    return (
      <BuildPassLookingTextarea
        as={AntInput.TextArea}
        $naked={naked}
        transparent={transparent}
        ref={ref}
        {...rest}
      />
    )
  }
)

Input.displayName = 'Input'
InputNumber.displayName = 'InputNumber'
TextArea.displayName = 'TextArea'
